/*
 * <store-locator-geolocate> custom element.
 *
 * Thin wrapper around a single icon button that resolves the visitor's
 * coordinates via navigator.geolocation. Used in both the full and compact
 * locator embeds:
 *
 *   - Full mode: dispatches `store-locator:geolocated` / `:geolocate-error`
 *     events that the parent <store-locator> listens for.
 *   - Compact mode: when `data-redirect-url` is set, navigates to that URL
 *     with `?lat=&lng=` appended on success. Errors are rendered into the
 *     element pointed at by `data-error-target`.
 *
 * Feature-detection at connect time keeps the button hidden when geolocation
 * isn't available (legacy browsers, non-secure contexts) so we never flash a
 * non-functional control.
 */

const GEOLOCATE_TIMEOUT_MS = 10_000;
const GEOLOCATE_MAX_AGE_MS = 60_000;
const COORD_PRECISION = 4;

class StoreLocatorGeolocate extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    if (!('geolocation' in navigator) || !window.isSecureContext) {
      this.button.hidden = true;
      return;
    }

    this.button.hidden = false;
    this.button.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.#handleClick);
  }

  #handleClick = () => {
    this.#setBusy(true);
    this.#clearError();
    navigator.geolocation.getCurrentPosition(
      (position) => this.#onSuccess(position),
      (error) => this.#onError(error),
      {
        enableHighAccuracy: false,
        timeout: GEOLOCATE_TIMEOUT_MS,
        maximumAge: GEOLOCATE_MAX_AGE_MS,
      }
    );
  };

  #setBusy(busy) {
    if (!this.button) return;
    this.button.disabled = busy;
    if (busy) this.button.setAttribute('aria-busy', 'true');
    else this.button.removeAttribute('aria-busy');
  }

  #onSuccess(position) {
    this.#setBusy(false);
    const lat = position.coords.latitude;
    const lng = position.coords.longitude;

    const redirect = this.dataset.redirectUrl;
    if (redirect) {
      const url = new URL(redirect, window.location.origin);
      url.searchParams.set('lat', lat.toFixed(COORD_PRECISION));
      url.searchParams.set('lng', lng.toFixed(COORD_PRECISION));
      window.location.assign(url.toString());
      return;
    }

    this.dispatchEvent(
      new CustomEvent('store-locator:geolocated', {
        detail: { lat, lng },
        bubbles: true,
      })
    );
  }

  #onError(error) {
    this.#setBusy(false);

    this.dispatchEvent(
      new CustomEvent('store-locator:geolocate-error', {
        detail: { code: error.code },
        bubbles: true,
      })
    );

    const targetSelector = this.dataset.errorTarget;
    if (!targetSelector) return;
    const target = document.querySelector(targetSelector);
    if (!target) return;

    const key =
      error.code === error.PERMISSION_DENIED
        ? 'errorDenied'
        : error.code === error.TIMEOUT
          ? 'errorTimeout'
          : 'errorUnavailable';
    const message = this.dataset[key] || '';
    if (!message) return;
    target.textContent = message;
    target.hidden = false;
  }

  #clearError() {
    const targetSelector = this.dataset.errorTarget;
    if (!targetSelector) return;
    const target = document.querySelector(targetSelector);
    if (!target) return;
    target.textContent = '';
    target.hidden = true;
  }
}

if (!customElements.get('store-locator-geolocate')) {
  customElements.define('store-locator-geolocate', StoreLocatorGeolocate);
}
